import React, { useState, useEffect } from 'react';
import {
  Grid, GridSpacing, Divider, Box,
} from '@material-ui/core';
import BaseChip from 'components/form/BaseChip';
import { SelectField, OptionType } from 'components/form/SelectField';
import { Column } from 'components/layout/Column';
import { Row } from 'components/layout/Row';
import {
  ReportSeriesRes, ReportSeriesVars, REPORT_SERIES, Kpi,
} from 'query/track';
import Arrows from '@images/ChipIcon/fast-rewind3.svg';
import ArrowDown from '@images/ChipIcon/fast-rewind.svg';
import ArrowUp from '@images/ChipIcon/fast-rewind2.svg';
import { ExportTemplateOrderingDirectionChoices } from 'models/template';
import { useTranslation, Trans } from 'react-i18next';
import { IconWrapper, Loader } from 'lib/icons';
import Error from 'components/layout/Error';
import { useQueryCached } from 'components/utils/graphql';
import { Alert } from '@material-ui/lab';
import { kpiPlatformIcons } from 'components/KpiPlatformIcons';
import { camelCaser } from '../utils/kpiLabelExtractor';
import { CACHE_AND_NETWORK } from '../../lib/cache';
import GridSortable from '../form/GridSortable';

type PropsIn = {
  teamId: string;
  kpi2: string[];
  kpiCounter: number;
  setKpiCounter: (n:number) => void;
  setKpiList: (kpis) => void;
  ordering: string;
  orderingDirection: ExportTemplateOrderingDirectionChoices;
  setOrdering: (key: string) => void;
  setOrderingDirection: (dir: ExportTemplateOrderingDirectionChoices) => void;
  filterObsolete?: boolean;
};

export function KpiChip(props: {
  label: string;
  isSelected: boolean;
  action: () => void;
  selectable: boolean;
  hoverDisabled?: boolean;
}) {
  const {
    label, isSelected, action, selectable, hoverDisabled,
  } = props;

  const [selected, setSelected] = useState(isSelected);
  const [hover, setHover] = useState(false);

  const classname = hoverDisabled
    ? `${selected ? 'selected-no-hover' : 'unselected-no-hover'}`
    : `${selected ? 'selected' : 'unselected'}`;
  const icon = selectable ? selected ? (
    <IconWrapper name="remove" size="smaller" color="primary" />
  ) : (
    <IconWrapper name="add" size="smaller" color="primary" />
  ) : null;

  return (
    <span onClick={action}>
      <BaseChip
        clickable={selectable}
        size="small"
        label={label}
        onClick={
          selectable
            ? () => {
              selected ? setSelected(false) : setSelected(true);
            }
            : () => {}
        }
        onMouseOver={
          selectable
            ? () => {
              hover ? setHover(false) : setHover(true);
            }
            : () => {}
        }
        className={classname}
        icon={icon}
      />
    </span>
  );
}

export type DividerWithTextProps = {
  text: string;
  spacing: GridSpacing;
};

export function DividerWithText(props: DividerWithTextProps) {
  const { text, spacing } = props;

  return (
    <Grid container alignItems="center" spacing={spacing}>
      <Grid item xs>
        <Divider />
      </Grid>
      <Grid item>{text}</Grid>
      <Grid item xs>
        <Divider />
      </Grid>
    </Grid>
  );
}

function OrdererChip(props: {
  id: string;
  kpi: any;
  orderingKey: string;
  orderingDirection: ExportTemplateOrderingDirectionChoices;
  handleSelection: (key) => void;
}) {
  const {
    id, kpi, orderingKey, orderingDirection, handleSelection,
  } = props;
  const { t } = useTranslation();

  // HR translations group
  const kpiGroup = kpi.group === 'HR zones' && ['hr', 'percentage'].includes(kpi.uom)
    ? 'main' : kpi.group;
  const liveOnly = kpi.group === 'live_only';

  return (
    <span
      id={id}
      key={`id${id}-${kpi.key}`}
    >
      <BaseChip
        id={id}
        key={`${id}-${kpi.key}`}
        clickable={!liveOnly}
        size="small"
        className={liveOnly ? 'orderer-chip--live-only' : undefined}
        label={t(`kpi2.${camelCaser(kpi.group === 'fixed' ? 'main' : kpiGroup, kpi.key)}.label`, kpi.label || kpi.key.split('_').join(' '))}
        onClick={liveOnly ? undefined : () => {
          handleSelection(kpi.key);
        }}
        icon={liveOnly ? undefined : (
          <Box pl={1} pt={1.2} pb={2}>
            <Column>
              <Row>
                {orderingKey !== kpi.key ? <Arrows /> : orderingDirection === '+' ? <ArrowUp /> : <ArrowDown />}
              </Row>
            </Column>
          </Box>
        )}
      />
    </span>
  );
}

function OrdererKpi(props: {
  kpi: any[];
  fixedKpis: any[];
  otherKpis: any[];
  setKpiSelected: (e: any) => void;
  ordering: string;
  orderingDirection: ExportTemplateOrderingDirectionChoices;
  setOrdering: (key: string) => void;
  setOrderingDirection: (dir: ExportTemplateOrderingDirectionChoices) => void;
}) {
  const {
    kpi, fixedKpis, otherKpis, setKpiSelected, ordering, orderingDirection, setOrdering, setOrderingDirection,
  } = props;
  const { t } = useTranslation();

  const hasLiveOnly = [...fixedKpis, ...otherKpis].some((curr) => curr.kpi.group === 'live_only');

  const setCurrentlySelectedWrap = (key: string) => {
    setOrdering(key);
    setOrderingDirection(orderingDirection === '+' ? '-' : '+');
  };

  const handleOrderChange = (arr: { key: number }[]) => {
    const newOrder = arr.reduce((acc: Kpi[], curr, index) => {
      const k = kpi.find((ki) => ki.id === curr.key);
      if (k) {
        acc.push({
          ...k,
          order: index + 4,
        });
      }
      return acc;
    }, []);

    setKpiSelected([...fixedKpis, ...newOrder]);
  };

  return (
    <Box>
      <Box pt={3} pb={2} padding={0}>
        <DividerWithText text={t('templates.labels.columnOrdering', 'COLUMN ORDERING AND DATA SORTING')} spacing={3} />
      </Box>
      {hasLiveOnly && (
        <Alert style={{ marginBottom: '10px' }} severity="info">
          {t(
            'templates.dialog.liveOnlyKpiWarning',
            'The instantaneous KPIs, shown greyed out, are not used as columns in the session and do not count '
            + 'towards the column limit. They are exported in the PDF, Excel and CSV files.',
          )}
        </Alert>
      )}
      <Column xs={12} style={{ marginBottom: '0.5rem' }}>
        {fixedKpis.map((curr) => (
          <OrdererChip
            id={curr.id}
            key={curr.id}
            kpi={curr.kpi}
            handleSelection={setCurrentlySelectedWrap}
            orderingKey={ordering}
            orderingDirection={orderingDirection}
          />
        ))}
      </Column>
      <Column xs={12} className="sortable-kpis-box">
        <GridSortable
          items={otherKpis
            .map((curr) => (
              <OrdererChip
                id={curr.id}
                key={curr.id}
                kpi={curr.kpi}
                handleSelection={setCurrentlySelectedWrap}
                orderingKey={ordering}
                orderingDirection={orderingDirection}
              />
            ))}
          onItemsMove={handleOrderChange}
        />
      </Column>
    </Box>
  );
}
const fixedOrderByKey = {
  athlete: 0,
  short_name: 1,
  jersey: 2,
};

export default function SelectableKpi(props: PropsIn) {
  const {
    teamId, kpiCounter, setKpiCounter, ordering, orderingDirection,
    setOrdering, setOrderingDirection, setKpiList, kpi2, filterObsolete,
  } = props;
  const { t } = useTranslation();
  const [fixedKpis, setFixedKpis] = useState<any[]>([]);
  const [otherKpis, setOtherKpis] = useState<any[]>([]);
  const [, setCounters] = useState({
    identifier: 0,
    main: 1,
    live_only: 0,
    met: 0,
    metabolic_power_events: 0,
    mech: 0,
    locomotion: 0,
    speed_zones: 0,
    '%_speed_zones': 0,
    power_zones: 0,
    hr_zones: 0,
    acc_zones: 0,
    dec_zones: 0,
    imu_events: 0,
  });
  const [kpiSelected, setKpiSelected] = useState<any[]>([]);

  const formatGroupStr = (group) => group.replaceAll(' ', '_').toLowerCase();

  const {
    loading,
    error,
    data: kpis,
  } = useQueryCached<ReportSeriesRes, ReportSeriesVars>(REPORT_SERIES, {
    variables: {
      teamId,
      filterObsolete,
      includeLiveOnly: true,
    },
    ...CACHE_AND_NETWORK,
    onCompleted: (data) => {
      const groups = {
        identifier: 0,
        main: 1,
        live_only: 0,
        met: 0,
        metabolic_power_events: 0,
        mech: 0,
        locomotion: 0,
        speed_zones: 0,
        '%_speed_zones': 0,
        power_zones: 0,
        hr_zones: 0,
        acc_zones: 0,
        dec_zones: 0,
        imu_events: 0,
      };

      const kpiData = data.reportSeries.map((obj) => {
        // moved hr kpis from main group to HR zones group
        return obj;
      });

      kpi2?.forEach((k) => {
        const kpi = kpiData.filter((d) => d.key === k)[0];
        if (kpi?.group && kpi.key !== 'average_time') {
          if (kpi.isIdentifier) {
            groups.identifier += 1;
          } else {
            const targetGroup = formatGroupStr(kpi.group);
            groups[targetGroup] = groups[targetGroup] + 1 || 1;
          }
        }
      });
      setCounters(groups);
    },
  });

  const identifKpis = (kpis?.reportSeries || []).filter((k) => k.isIdentifier).map((idKpi) => idKpi.key);

  const visibleSeries = [...(kpis?.reportSeries || [])];

  const percIdx = visibleSeries.findIndex((k) => k.key === 'power_aer_perc');
  if (percIdx !== -1) {
    const [perc] = visibleSeries.splice(percIdx, 1);
    visibleSeries.push(perc);
  }

  const filteredKpis = visibleSeries.reduce((acc, curr) => {
    // skip iteration (to remove ext work -)
    if (curr.key === 'external_work_negative') return acc;
     else if (curr.isIdentifier) {
      if (!acc.identifier) {
        acc.identifier = [];
      }
      acc.identifier.push(curr);
    } else {
      if (!acc[curr.group]) {
        acc[curr.group] = [];
      }
      acc[curr.group].push(curr);
    }
    return acc;
  }, {} as { [group: string]: any[] }) || [];

  if (filteredKpis.live_only) {
    const liveOnlyOrder = ['speed', 'power', 'power_aer', 'power_aer_perc', 'cardio', 'cardio_percentual'];
    const liveOnlyIndex = (kpi) => {
      const index = liveOnlyOrder.indexOf(kpi.key);
      return index === -1 ? liveOnlyOrder.length : index;
    };
    filteredKpis.live_only.sort((a, b) => liveOnlyIndex(a) - liveOnlyIndex(b));
  }

  useEffect(() => {
    const kpiarr = [];
    const allKpis = kpis?.reportSeries.concat({ key: 'average_time', unit: 's', group: 'main' });

    Array.from(new Set(kpi2)).forEach((e, idx) => {
      allKpis?.forEach((curr) => {
        if (e === curr.key) {
          const sort = true; // da modificare quando verranno implementate icon per il sort
          // 'duration' should always be positioned after all identif kpis.
          const durOrd = curr.key === 'average_time' ? 3 : undefined;
          const kpi = {
            kpi: curr,
            order: durOrd ?? fixedOrderByKey[e] ?? idx,
            sort,
            id: `id${idx}`,
          };
          kpiarr.push(kpi);
        }
      });
    });
    setKpiSelected(kpiarr);
  }, [kpis]);

  useEffect(() => {
    if (kpiSelected.length) {
      const sortedKpis = kpiSelected.slice().sort((k1, k2) => k1.order - k2.order);
      const fixedK = [...sortedKpis.filter((obj) => [...identifKpis, 'average_time'].includes(obj.kpi.key))];
      const fixedKpiKeys = fixedK.map((fk) => fk.kpi.key);
      const otherK = sortedKpis.filter((obj) => !fixedKpiKeys.includes(obj.kpi.key));

      setFixedKpis(fixedK);
      setOtherKpis(otherK);
      setKpiList([...fixedK.map((k) => k.kpi.key), ...otherK.map((k) => k.kpi.key)]);
      setKpiCounter(kpiSelected.length);
    }
  }, [kpiSelected]);

  const seriesByKey = (kpis?.reportSeries || [])
    .reduce((acc, curr) => ({ ...acc, [curr.key]: curr }), {} as { [key: string]: any });

  const groupTitles: [string, string][] = [
    ['identifier', t('templates.labels.athIdentification', 'athlete identification')],
    ['live_only', t('kpi2.live_only.title', 'instantaneous data')],
    ['main', t('kpi2.main.title', 'basic')],
    ['speed zones', t('kpi2.speedZones.title', 'speed zones')],
    ['% speed zones', t('kpi2.%SpeedZones.title', '% speed zones')],
    ['acc zones', t('kpi2.accZones.title', 'acc zones')],
    ['dec zones', t('kpi2.decZones.title', 'dec zones')],
    ['met', t('kpi2.met.title', 'met')],
    ['power zones', t('kpi2.powerZones.title', 'power zones')],
    ['metabolic power events', t('kpi2.metabolicPowerEvents.title', 'metabolic power events')],
    ['mech', t('kpi2.mech.title', 'mech')],
    ['locomotion', t('kpi2.locomotion.title', 'locomotion')],
    ['IMU events', t('kpi2.IMUEvents.title', 'imu events')],
    ['HR zones', t('kpi2.HRZones.title', 'heart rate')],
  ];

  const kpiOptions: OptionType[] = [];
  const keysByGroupTitle: { [title: string]: string[] } = {};
  groupTitles.forEach(([group, title]) => {
    if (group === 'main') {
      kpiOptions.push({
        id: 'average_time',
        value: t('table.duration', 'duration'),
        groupBy: title,
        disabled: true,
      });
    }
    (filteredKpis[group] || []).forEach((kpi) => {
      if (kpi.key === 'average_time') return;
      const kpiGroup = group === 'identifier' ? 'main' : group;
      kpiOptions.push({
        id: kpi.key,
        value: t(`kpi2.${camelCaser(kpiGroup, kpi.key)}.label`, kpi.label || kpi.key.split('_').join(' ')),
        groupBy: title,
        disabled: kpi.key === 'athlete',
      });
      keysByGroupTitle[title] = [...(keysByGroupTitle[title] || []), kpi.key];
    });
  });

  const selectedOptions = kpiOptions
    .filter((op) => op.id === 'average_time' || kpiSelected.some((sk) => sk.kpi.key === op.id));

  const countGroups = (list: any[]) => {
    const groups = {
      identifier: 0,
      main: 1,
      live_only: 0,
      met: 0,
      metabolic_power_events: 0,
      mech: 0,
      locomotion: 0,
      speed_zones: 0,
      '%_speed_zones': 0,
      power_zones: 0,
      hr_zones: 0,
      acc_zones: 0,
      dec_zones: 0,
      imu_events: 0,
    };
    list.forEach(({ kpi }) => {
      if (kpi?.group && kpi.key !== 'average_time') {
        if (kpi.isIdentifier) {
          groups.identifier += 1;
        } else {
          const targetGroup = formatGroupStr(kpi.group);
          groups[targetGroup] = groups[targetGroup] + 1 || 1;
        }
      }
    });
    return groups;
  };

  const applySelection = (wantedKeys: string[]) => {
    const asked = Array.from(new Set(wantedKeys));
    const keys = kpiSelected.some((sk) => sk.kpi.key === 'athlete') && !asked.includes('athlete')
      ? ['athlete', ...asked]
      : asked;
    const byKey = kpiSelected.reduce((acc, sk) => ({ ...acc, [sk.kpi.key]: sk }), {} as { [k: string]: any });
    let nextOrder = kpiSelected.reduce((max, sk) => (sk.order > max ? sk.order : max), 0) + 1;

    const next: any[] = [];
    keys.forEach((key) => {
      if (byKey[key]) {
        next.push(byKey[key]);
        return;
      }
      const kpi = seriesByKey[key];
      if (!kpi) return;
      next.push({
        kpi, order: fixedOrderByKey[key] ?? nextOrder, sort: true, id: `id${nextOrder}`,
      });
      nextOrder += 1;
    });

    const duration = kpiSelected.find((sk) => sk.kpi.key === 'average_time');
    if (duration && !next.some((sk) => sk.kpi.key === 'average_time')) next.push(duration);

    setKpiSelected(next);
    setCounters(countGroups(next));

    if (!next.some((sk) => sk.kpi.key === ordering)) {
      const firstIdentif = next.slice().sort((k1, k2) => k1.order - k2.order)
        .find((sk) => identifKpis.includes(sk.kpi.key));
      if (firstIdentif) {
        setOrdering(firstIdentif.kpi.key);
        setOrderingDirection('+');
      }
    }
  };

  const toggleGroup = (title: string) => {
    const groupKeys = (keysByGroupTitle[title] || []).filter((k) => k !== 'athlete');
    const selectedKeys = kpiSelected.map((sk) => sk.kpi.key);
    const allIn = groupKeys.every((k) => selectedKeys.includes(k));
    applySelection(allIn
      ? selectedKeys.filter((k) => !groupKeys.includes(k))
      : [...selectedKeys, ...groupKeys.filter((k) => !selectedKeys.includes(k))]);
  };

  if (error) return <Error />;
  if (loading) return <Loader />;

  return (
    <Box>
      <Box sx={{ marginY: '10px' }}>
        <Box pt={3} pb={2}>
          <DividerWithText text={t('templates.labels.selectKpi', 'SELECT KPI')} spacing={3} />
        </Box>
        <SelectField
          autocomplete
          multiple
          disableCloseOnSelect
          id="template-kpi"
          fullWidth
          label={t('stats.kpi', 'KPI')}
          options={kpiOptions}
          value={selectedOptions}
          optionIcons={kpiPlatformIcons}
          onGroupClick={toggleGroup}
          onChange={(v) => applySelection((v as OptionType[]).map((op) => op.id))}
        />

        { kpiCounter > 9
          && (
          <Alert
            style={{ marginTop: '20px' }}
            severity="warning"
          >
            <Trans
              i18nKey="templates.dialog.templateAlert"
              defaults="The maximum number of parameters displayable in the PDF <br/>
            (10 for the portrait mode and 12 for the landscape mode) has been reached. <br/>
            The maximum number of parameters displayable on the webapp is 15. <br/>
            Additional parameters will be available only in CSV or Excel exports."
            />
          </Alert>
          )}

        <OrdererKpi
          kpi={kpiSelected}
          fixedKpis={fixedKpis}
          otherKpis={otherKpis}
          setKpiSelected={setKpiSelected}
          ordering={ordering}
          orderingDirection={orderingDirection}
          setOrdering={setOrdering}
          setOrderingDirection={setOrderingDirection}
        />
      </Box>
    </Box>
  );
}
